import React, { useMemo } from "react";
import moment from "moment-timezone";
import { type ActiveEvent } from "kadow-jetpack-model";
import KC_Card from "../../../../../designsystem/KC_Card/KC_Card";
import KC_UIText from "../../../../../designsystem/typography/KC_UIText/KC_UIText";
import Dot from "../../../../../components/Dot/Dot";
import GameCardPreview from "../../../../23_GameCardPreview/GameCardPreview";
import TSUtils from "../../../../../utils/helpers/TSUtils";
import { tt } from "../../../../../utils/i18n/i18n";
import { getDisplayNameForGameType } from "../../../../../utils/helpers/getDisplayNameForGameType";
import { useEventsSelector } from "../../../../../redux/actions/wheel";
import { useJetpackConfigSelector } from "../../../../../redux/jetpackConfig";
import {
  type GameListItem,
  getEventCardData,
  getGameThumbnail
} from "./gameListTypes";

interface GameCardProps {
  item: GameListItem;
  showScheduledExplanation: boolean;
  canDeleteGames: boolean;
  onDeleteGame: () => void;
  onDeleteEvent: () => void;
}

function EventDates(props: { ae: ActiveEvent }) {
  return (
    <div>
      {tt("event_display_from")}{" "}
      <KC_UIText weight="bold" text={TSUtils.formatDate(props.ae.sts)} />{" "}
      {tt("event_display_to")}{" "}
      <KC_UIText weight="bold" text={TSUtils.formatDate(props.ae.ets)} />
    </div>
  );
}

export default function GameCard(props: GameCardProps) {
  const events = useEventsSelector();
  const config = useJetpackConfigSelector();
  const nowTs = useMemo(() => moment().unix(), []);

  if (props.item.type === "game") {
    const thumbnail = getGameThumbnail(props.item.game);

    if (!thumbnail) {
      return null;
    }

    return (
      <KC_Card
        title={getDisplayNameForGameType(props.item.game.name)}
        boxShadow={false}
        useTwicPics={true}
        isGameCardPreview={true}
        forceShowHeader={true}
        className="border"
        canDelete={props.canDeleteGames}
        isSelected={true}
        handleDelete={props.onDeleteGame}
        children={
          <>
            <GameCardPreview
              gameCardConfig={thumbnail.config}
              eventId={thumbnail.eventId}
            />
            <div className="d-flex align-items-center">
              <Dot color="green" />
              <KC_UIText
                className="ms-2"
                weight="reg"
                text={tt("active_permanently")}
              />
            </div>
          </>
        }
      />
    );
  }

  const { ae } = props.item;
  const cardData = getEventCardData(ae, events, config, nowTs);

  if (!cardData) {
    return null;
  }

  /*
    Scheduled events are time-bound (they always carry a reasonable ets), so we never
    treat them as permanent: their end date is always shown instead of "active permanently".
  */
  const showAsPermanent = cardData.eventConfig.permanent && !ae.s;
  const isLive = showAsPermanent || cardData.isOngoing;

  return (
    <KC_Card
      title={getDisplayNameForGameType(ae.gameType || "")}
      boxShadow={false}
      description=""
      useTwicPics={true}
      isGameCardPreview={true}
      className="border"
      canDelete={true}
      isSelected={true}
      handleDelete={props.onDeleteEvent}
      children={
        <>
          <GameCardPreview
            gameCardConfig={cardData.cardConfig}
            eventId={ae.id}
          />
          <div className="d-flex align-items-center">
            <Dot color={isLive ? "green" : "red"} />
            <KC_UIText
              className="ml-1 ms-2"
              weight="reg"
              text={
                showAsPermanent ? (
                  <KC_UIText weight="reg" text={tt("active_permanently")} />
                ) : (
                  <EventDates ae={ae} />
                )
              }
            />
          </div>
          {props.showScheduledExplanation && !!ae.s && (
            <div className="mt-1">
              <KC_UIText weight="reg" text={tt("scheduled_explanation")} />
            </div>
          )}
        </>
      }
    />
  );
}
